import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 心理学 - 横屏「实验室」风格
 *
 * 视觉特点：明亮干净的学术白底、结构化分区、Before/After对比、统计数据卡片
 * 适合：经典心理学实验、认知偏差解析、社会心理学现象等内容
 * 尺寸：1920x1080 横屏
 */

interface ExperimentPhase {
  phase: string;
  description: string;
  icon: string;
}

interface StatCard {
  value: string;
  label: string;
  color: string;
}

interface PsychologyLabProps {
  experimentName: string;
  researcher: string;
  year: string;
  hypothesis: string;
  phases: ExperimentPhase[];
  result: string;
  stats: StatCard[];
  insight: string;
  applicationTip: string;
}

const DEFAULT_PROPS: PsychologyLabProps = {
  experimentName: '棉花糖实验',
  researcher: 'Walter Mischel',
  year: '1972',
  hypothesis: '能够延迟满足的儿童，未来的学业和社会表现会更好',
  phases: [
    { phase: '准备', description: '给儿童一块棉花糖，告知等 15 分钟可获得两块', icon: '🍬' },
    { phase: '观察', description: '研究者离开房间，隐藏摄像头记录儿童行为', icon: '📹' },
    { phase: '结果', description: '约 1/3 儿童成功等待，2/3 提前吃掉', icon: '📊' },
    { phase: '追踪', description: '数十年后追踪发现：延迟满足者 SAT 平均高 210 分', icon: '📈' },
  ],
  result: '延迟满足能力与自控力、学业成就、健康水平显著正相关',
  stats: [
    { value: '600+', label: '参与儿童', color: '#6366f1' },
    { value: '40年', label: '追踪时长', color: '#0891b2' },
    { value: '210分', label: 'SAT 差距', color: '#dc2626' },
    { value: '1/3', label: '成功延迟', color: '#16a34a' },
  ],
  insight: '自控力不是天赋，而是可以训练的技能——关键在于转移注意力策略',
  applicationTip: '面对诱惑时，尝试把注意力从"奖励本身"转移到"等待的策略"上',
};

export const PsychologyLabTemplate: React.FC<Partial<PsychologyLabProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Header (0-40)
  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 100 } });

  // Hypothesis (40-80)
  const hypoOpacity = interpolate(frame, [40, 65], [0, 1], { extrapolateRight: 'clamp' });

  // Phases (80-280)
  const phaseStart = 80;
  const phaseInterval = 50;

  // Stats (280-360)
  const statsStart = 280;

  // Result + Insight (360-440)
  const resultOpacity = interpolate(frame, [360, 390], [0, 1], { extrapolateRight: 'clamp' });
  const insightOpacity = interpolate(frame, [400, 430], [0, 1], { extrapolateRight: 'clamp' });

  // Application (440-500)
  const appOpacity = interpolate(frame, [440, 470], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background: '#f8fafc',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: '#0f172a',
        overflow: 'hidden',
      }}
    >
      {/* Subtle lab grid background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Header - experiment info strip */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 120,
          background: 'white',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          padding: '0 48px',
          justifyContent: 'space-between',
          opacity: headerProgress,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div
            style={{
              padding: '4px 14px',
              background: '#ede9fe',
              borderRadius: 6,
              fontSize: 22,
              fontWeight: 600,
              color: '#6366f1',
              letterSpacing: '0.05em',
            }}
          >
            PSYCHOLOGY LAB
          </div>
          <h1 style={{ fontSize: 42, fontWeight: 800, margin: 0 }}>{p.experimentName}</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, fontSize: 22, color: '#64748b' }}>
          <span>👤 {p.researcher}</span>
          <span>📅 {p.year}</span>
        </div>
      </div>

      {/* LEFT PANEL — Experiment phases */}
      <div
        style={{
          position: 'absolute',
          top: 150,
          left: 48,
          width: '48%',
          bottom: 48,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Hypothesis */}
        <div
          style={{
            padding: '20px 24px',
            background: '#eff6ff',
            borderRadius: 14,
            border: '1px solid #bfdbfe',
            marginBottom: 20,
            opacity: hypoOpacity,
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 600, color: '#3b82f6', marginBottom: 6, letterSpacing: '0.08em' }}>
            HYPOTHESIS 假设
          </div>
          <div style={{ fontSize: 26, lineHeight: 1.5, color: '#1e40af' }}>{p.hypothesis}</div>
        </div>

        {/* Phase cards */}
        <div style={{ fontSize: 24, fontWeight: 600, color: '#94a3b8', marginBottom: 30, marginTop: 20, letterSpacing: '0.08em' }}>
          EXPERIMENT PHASES 实验流程
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
          {p.phases.map((phase, i) => {
            const start = phaseStart + i * phaseInterval;
            const cardProgress = spring({
              frame: frame - start,
              fps,
              from: 0,
              to: 1,
              config: { damping: 14, stiffness: 130, mass: 0.5 },
            });
            const isActive = interpolate(frame, [start, start + phaseInterval, start + phaseInterval + 1], [0, 1, 1], { extrapolateRight: 'clamp' }) > 0.5;

            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: '16px 20px',
                  background: 'white',
                  borderRadius: 14,
                  border: isActive ? '2px solid #6366f1' : '1px solid #e2e8f0',
                  boxShadow: isActive ? '0 4px 12px rgba(99,102,241,0.1)' : '0 1px 4px rgba(0,0,0,0.03)',
                  opacity: cardProgress,
                  transform: `translateY(${(1 - cardProgress) * 20}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: isActive ? '#eef2ff' : '#f8fafc',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: 30,
                    flexShrink: 0,
                  }}
                >
                  {phase.icon}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        fontSize: 24,
                        fontWeight: 700,
                        color: '#6366f1',
                        background: '#eef2ff',
                        padding: '2px 8px',
                        borderRadius: 4,
                      }}
                    >
                      STEP {i + 1}
                    </span>
                    <span style={{ fontSize: 26, fontWeight: 700 }}>{phase.phase}</span>
                  </div>
                  <div style={{ fontSize: 24, color: '#64748b', marginTop: 4, lineHeight: 1.4 }}>
                    {phase.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT PANEL — Stats + Results + Insight */}
      <div
        style={{
          position: 'absolute',
          top: 150,
          right: 48,
          width: '45%',
          bottom: 48,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {/* Stats grid */}
        <div style={{ fontSize: 26, fontWeight: 600, color: '#94a3b8', marginBottom: 0, letterSpacing: '0.08em' }}>
          KEY DATA 关键数据
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {p.stats.map((stat, i) => {
            const start = statsStart + i * 20;
            const progress = spring({
              frame: frame - start,
              fps,
              from: 0,
              to: 1,
              config: { damping: 12, stiffness: 140, mass: 0.5 },
            });

            return (
              <div
                key={i}
                style={{
                  padding: '20px',
                  background: 'white',
                  borderRadius: 14,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                  textAlign: 'center',
                  opacity: progress,
                  transform: `scale(${0.9 + progress * 0.1})`,
                }}
              >
                <div style={{ fontSize: 36, fontWeight: 900, color: stat.color }}>{stat.value}</div>
                <div style={{ fontSize: 22, color: '#64748b', marginTop: 6 }}>{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Result */}
        <div
          style={{
            padding: '20px 24px',
            background: '#f0fdf4',
            borderRadius: 14,
            border: '1px solid #bbf7d0',
            opacity: resultOpacity,
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 600, color: '#16a34a', marginBottom: 6, letterSpacing: '0.08em' }}>
            CONCLUSION 结论
          </div>
          <div style={{ fontSize: 26, lineHeight: 1.5, color: '#15803d' }}>{p.result}</div>
        </div>

        {/* Insight */}
        <div
          style={{
            padding: '20px 24px',
            background: '#fefce8',
            borderRadius: 14,
            border: '1px solid #fde68a',
            opacity: insightOpacity,
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 600, color: '#ca8a04', marginBottom: 6, letterSpacing: '0.08em' }}>
            INSIGHT 洞察
          </div>
          <div style={{ fontSize: 26, lineHeight: 1.5, color: '#a16207' }}>💡 {p.insight}</div>
        </div>

        {/* Application tip */}
        <div
          style={{
            padding: '20px 24px',
            background: 'white',
            borderRadius: 14,
            border: '2px solid #6366f1',
            opacity: appOpacity,
            marginTop: 'auto',
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 600, color: '#6366f1', marginBottom: 6, letterSpacing: '0.08em' }}>
            HOW TO APPLY 如何应用
          </div>
          <div style={{ fontSize: 20, lineHeight: 1.6, color: '#334155' }}>
            🎯 {p.applicationTip}
          </div>
        </div>
      </div>

      {/* Bottom progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 510], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #6366f1, #a78bfa)',
        }}
      />
    </AbsoluteFill>
  );
};
